import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertCircle, MapPin, CheckCircle2 } from "lucide-react";
import axios from "axios";
import View_worker_supervisor from "./View_worker_supervisor";
import { handleWorkerUpdated, fetchData, fetchDepartments, handleAssign } from "./Supervisorhelper";
import { useAuth } from "../context/auth-context";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AssignDepartment = ({ userId, onAssign }) => {
  const {
    token,
    fetchNearestCompany,
    fetchDepartmentsBySkills,
    assignWorkers,
    freezeDepartmentApi,
    updateWorkerDepartmentApi,
  } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [collapsedDepts, setCollapsedDepts] = useState({});

  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [departmentsError, setDepartmentsError] = useState(null);
  const [departmentCounts, setDepartmentCounts] = useState({});
  const [assignError, setAssignError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  const [showworkermodal, setshowworkermodal] = useState(false);

  const [workerList, setWorkerList] = useState([
    { Totalworker: "0", pending: "0", allotted: "0", inProcess: "0" },
    [],
  ]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [workersError, setWorkersError] = useState(null);

  // ── Geolocation state
  const [coords, setCoords] = useState({ latitude: null, longitude: null });
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationChecked, setLocationChecked] = useState(false);

  // ── Derived worker counts
  const totalWorkers = parseInt(workerList[0][0]?.Totalworker, 10) || 0;
  const pendingworker = parseInt(workerList[0][0]?.pending, 10) || 0;
  const allottedworker = parseInt(workerList[0][0]?.allotted, 10) || 0;
  const inprocessworker = parseInt(workerList[0][0]?.inProcess, 10) || 0;

  // ── Geolocation
  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      setLocationDenied(true);
      setLocationChecked(true);
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCoords({ latitude, longitude });
        try {
          localStorage.setItem(
            "userLocation",
            JSON.stringify({ latitude, longitude, timestamp: Date.now() })
          );
        } catch (_) {}
        setLocationDenied(false);
        setLocationLoading(false);
        setLocationChecked(true);
      },
      () => {
        setLocationDenied(true);
        setLocationLoading(false);
        setLocationChecked(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setLocationDenied(true);
      setLocationChecked(true);
      return;
    }

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then((status) => {
        if (status.state === "granted") {
          requestLocation();
        } else if (status.state === "prompt") {
          setCoords({ latitude: null, longitude: null });
          try { localStorage.removeItem("userLocation"); } catch (_) {}
          setLocationChecked(true);
        } else if (status.state === "denied") {
          setCoords({ latitude: null, longitude: null });
          try { localStorage.removeItem("userLocation"); } catch (_) {}
          setLocationDenied(true);
          setLocationChecked(true);
        }
        status.onchange = () => {
          if (status.state === "granted") {
            setLocationDenied(false);
            requestLocation();
          } else if (status.state === "denied") {
            setCoords({ latitude: null, longitude: null });
            try { localStorage.removeItem("userLocation"); } catch (_) {}
            setLocationDenied(true);
            setLocationChecked(true);
          }
        };
      });
    } else {
      try {
        const stored = JSON.parse(localStorage.getItem("userLocation"));
        if (stored?.latitude && stored?.longitude) {
          setCoords({ latitude: stored.latitude, longitude: stored.longitude });
        }
      } catch (_) {}
      setLocationChecked(true);
    }
  }, []);

  // ── Collapse departments when worker modal opens
  useEffect(() => {
    if (showworkermodal && workerList[1]?.length > 0) {
      setCollapsedDepts((prev) => {
        const newEntries = {};
        workerList[1].forEach((w) => {
          const key = `modal_${w.department || "Not Assigned"}`;
          if (!(key in prev)) newEntries[key] = true;
        });
        return { ...prev, ...newEntries };
      });
    }
  }, [showworkermodal, workerList]);

  // ── Fetch company
  useEffect(() => {
    if (!locationChecked) return;
    fetchData({ setLoading, setError, coords, setCompanies, setSelectedCompany, fetchNearestCompany });
  }, [token, locationChecked, coords.latitude, coords.longitude]);

  // ── Fetch departments whenever selectedCompany changes
  useEffect(() => {
    if (!selectedCompany) {
      setDepartments([]);
      setDepartmentCounts({});
      return;
    }
    fetchDepartments({
      setLoadingDepartments,
      setDepartmentsError,
      selectedCompany,
      setDepartments,
      setDepartmentCounts,
      fetchDepartmentsBySkills,
    });
  }, [selectedCompany, token]);

  // ── Fetch workers
  const fetchWorkers = useCallback(
    async (companyId) => {
      if (!companyId) {
        setWorkerList([[{ Totalworker: "0", pending: "0", allotted: "0", inProcess: "0" }], []]);
        setWorkersError(null);
        return;
      }
      try {
        setLoadingWorkers(true);
        setWorkersError(null);
        const workerRes = await axios.post(
          `${BASE_URL}/worker-allotment/recent-workers`,
          { companyId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = workerRes.data;
        if (data && data.status && Array.isArray(data.workers)) {
          const summary = {
            Totalworker: String(data.total ?? data.workers.length),
            pending: String(data.pendingCount ?? 0),
            allotted: String(data.allottedCount ?? 0),
            inProcess: String(data.inprocessCount ?? 0),
          };
          setWorkerList([[summary], data.workers]);
        } else {
          throw new Error(data?.msg || data?.error || "Invalid worker data received");
        }
      } catch (err) {
        setWorkersError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            "Failed to load workers"
        );
        setWorkerList([[{ Totalworker: "0", pending: "0", allotted: "0", inProcess: "0" }], []]);
      } finally {
        setLoadingWorkers(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchWorkers(selectedCompany);
  }, [selectedCompany, fetchWorkers]);

  // ── Handlers
  const handleViewWorkers = () => {
    setshowworkermodal(true);
    fetchWorkers(selectedCompany);
  };

  const handleCloseWorkerModal = () => {
    setshowworkermodal(false);
    fetchWorkers(selectedCompany);
  };

  const handleCompanyChange = (e) => {
    setSelectedCompany(e.target.value);
    setAssignError(null);
  };

  const handleDepartmentCountChange = (deptId, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setDepartmentCounts((prev) => ({ ...prev, [deptId]: value }));
    setAssignError(null);
  };

  const allocatedTotal = Object.values(departmentCounts).reduce(
    (sum, v) => sum + (parseInt(v, 10) || 0),
    0
  );
  const isOverLimit = allocatedTotal > pendingworker;

  // ── Guards
  if (!locationChecked || locationLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 p-4 z-50">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-[90vw] sm:max-w-sm lg:max-w-md p-6 sm:p-8 flex flex-col items-center gap-4 text-center">
          <div>
            <h2 className="text-sm sm:text-base font-semibold text-gray-900">Getting your location</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">This can take a few seconds</p>
          </div>
          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (!coords.latitude && !coords.longitude) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 p-4 z-50">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-[90vw] sm:max-w-sm lg:max-w-md p-5 sm:p-6 flex flex-col items-center gap-4 text-center">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${locationDenied ? "bg-red-50" : "bg-blue-50"}`}>
            <MapPin className={`h-6 w-6 ${locationDenied ? "text-red-500" : "text-blue-500"}`} />
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-semibold text-gray-900">Location Required</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
              {locationDenied
                ? "Location access is blocked. Please enable location in your browser settings."
                : "This page requires your location to proceed. Please allow access when prompted."}
            </p>
          </div>
          {!locationDenied && (
            <button
              onClick={requestLocation}
              disabled={locationLoading}
              className="w-full py-2.5 text-sm font-semibold bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <MapPin className="h-4 w-4" />
              Enable Location
            </button>
          )}
          {locationDenied && (
            <p className="text-xs text-gray-400">After enabling location, reload this page to continue.</p>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
        <div className="max-w-[90vw] sm:max-w-md w-full p-5 sm:p-6 bg-red-50 rounded-lg shadow-sm text-center">
          <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // ── Main render
  return (
    <div className="fixed inset-0 flex items-start justify-center bg-gray-100 p-2 xs:p-3 sm:p-5 overflow-y-auto pt-6 sm:pt-16 lg:pt-20 pb-6">
      <div className="w-full max-w-[95vw] xs:max-w-sm sm:max-w-md lg:max-w-3xl xl:max-w-4xl bg-white shadow-lg rounded-2xl">

        {/* Header */}
        <div className="px-3 xs:px-4 lg:px-8 pt-12 lg:pt-6 pb-0 text-center">
          <h2 className="text-sm xs:text-base sm:text-lg lg:text-xl font-semibold text-gray-900 tracking-wide">
            Assign Department
          </h2>
        </div>

        <div className="px-3 xs:px-4 py-3 sm:px-5 sm:py-4 lg:px-8 lg:py-6 flex flex-col gap-3 lg:gap-5">

          {/* Company display */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Company
            </label>
            {companies.length === 1 ? (
              <div className="px-3 py-2 lg:py-2.5 bg-gray-50 rounded-lg text-sm lg:text-base font-medium text-gray-800 border border-gray-200 truncate">
                {companies[0].companyName} ({companies[0].companyCode})
              </div>
            ) : (
              <select
                value={selectedCompany}
                onChange={handleCompanyChange}
                disabled={companies.length === 0}
                className="w-full px-3 py-2 lg:py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm lg:text-base text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {companies.length === 0
                  ? <option value="">No companies available</option>
                  : companies.map((c) => (
                      <option key={c.companyId} value={c.companyId}>
                        {c.companyName} ({c.companyCode})
                      </option>
                    ))}
              </select>
            )}
          </div>

          {/* Workers summary */}
          {selectedCompany && (
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              {loadingWorkers ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 px-1 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                  Loading workers...
                </div>
              ) : workersError ? (
                <p className="text-red-600 text-xs">{workersError}</p>
              ) : (
                <div className="flex flex-col gap-2 w-full sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                  {/* Stats — 2x2 on the smallest phones, single row from sm and up, wrapping if space runs out */}
                  <div className="grid grid-cols-2 xs:grid-cols-4 gap-1.5 sm:gap-2 lg:gap-3 w-full sm:w-auto sm:flex sm:flex-wrap sm:items-center">
                    <div className="flex flex-col sm:flex-row items-center sm:gap-1.5 bg-white px-1.5 py-1.5 sm:px-2 sm:py-2 lg:py-2.5 rounded-md sm:rounded-lg border border-gray-300 min-w-0">
                      <span className="text-[10px] sm:text-sm lg:text-base font-semibold text-gray-500 sm:text-gray-700 leading-tight truncate">
                        <span className="sm:hidden">Total Worker Scanned</span>
                        <span className="hidden sm:inline">Total Worker Scanned:</span>
                      </span>
                      <span className="text-xs sm:text-sm lg:text-base font-bold sm:font-semibold text-indigo-600 leading-tight">
                        {totalWorkers}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center sm:gap-1.5 bg-white px-1.5 py-1.5 sm:px-2 sm:py-2 lg:py-2.5 rounded-md sm:rounded-lg border border-gray-300 min-w-0">
                      <span className="text-[10px] sm:text-sm lg:text-base font-semibold text-gray-500 sm:text-gray-700 leading-tight truncate">
                        <span className="sm:hidden">Allotment Pending</span>
                        <span className="hidden sm:inline">Allotment Pending:</span>
                      </span>
                      <span className="text-xs sm:text-sm lg:text-base font-bold sm:font-semibold text-indigo-600 leading-tight">
                        {pendingworker}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center sm:gap-1.5 bg-white px-1.5 py-1.5 sm:px-2 sm:py-2 lg:py-2.5 rounded-md sm:rounded-lg border border-gray-300 min-w-0">
                      <span className="text-[10px] sm:text-sm lg:text-base font-semibold text-gray-500 sm:text-gray-700 leading-tight truncate">
                        <span className="sm:hidden">Allocation In Process</span>
                        <span className="hidden sm:inline">Allocation In Process:</span>
                      </span>
                      <span className="text-xs sm:text-sm lg:text-base font-bold sm:font-semibold text-indigo-600 leading-tight">
                        {inprocessworker}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center sm:gap-1.5 bg-white px-1.5 py-1.5 sm:px-2 sm:py-2 lg:py-2.5 rounded-md sm:rounded-lg border border-gray-300 min-w-0">
                      <span className="text-[10px] sm:text-sm lg:text-base font-semibold text-gray-500 sm:text-gray-700 leading-tight truncate">
                        <span className="sm:hidden">Allotted</span>
                        <span className="hidden sm:inline">Allotted:</span>
                      </span>
                      <span className="text-xs sm:text-sm lg:text-base font-bold sm:font-semibold text-indigo-600 leading-tight">
                        {allottedworker}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleViewWorkers}
                    className="bg-purple-500 text-white px-3 sm:px-4 lg:px-5 py-2 sm:py-2 lg:py-2.5 rounded-lg hover:bg-purple-600 active:scale-95 transition-all text-xs sm:text-sm lg:text-base font-medium shrink-0 whitespace-nowrap w-full sm:w-auto"
                  >
                    View Workers
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Department allocation */}
          {selectedCompany && (
            <div className="flex flex-col gap-2 lg:gap-3">
              <label className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Department Allocation
              </label>
              {loadingDepartments ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                </div>
              ) : departmentsError ? (
                <p className="text-red-600 text-sm">{departmentsError}</p>
              ) : departments.length === 0 ? (
                <p className="text-sm text-gray-500">No departments available</p>
              ) : (
                <div className="flex flex-col gap-2 lg:gap-3 max-h-[40vh] sm:max-h-none overflow-y-auto pr-0.5">
                  {departments.map((d) => {
                    const value = departmentCounts[d._id] ?? "";
                    const hasValue = parseInt(value, 10) > 0;
                    const showError = isOverLimit && hasValue;
                    return (
                      <div key={d._id} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 py-2 lg:py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="text-sm lg:text-base text-gray-700 truncate">{d.name}</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={value}
                            onChange={(e) => handleDepartmentCountChange(d._id, e.target.value)}
                            placeholder="0"
                            className={`w-14 sm:w-16 lg:w-20 shrink-0 px-2 py-1 lg:py-1.5 text-sm lg:text-base text-center bg-white border rounded-md focus:outline-none focus:ring-2 ${
                              showError
                                ? "border-red-500 focus:ring-red-400 focus:border-red-500"
                                : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                            }`}
                          />
                        </div>
                        {showError && (
                          <p className="text-red-600 text-xs px-1">
                            Exceeds available workers (max {pendingworker} total)
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Assign button */}
          <button
            onClick={() => handleAssign({
              selectedCompany, allocatedTotal, pendingworker, setAssignError, setShowErrorModal,
              setAssigning, departments, departmentCounts, setDepartmentCounts, onAssign,
              setAssignResult, setShowSuccessModal, fetchWorkers, assignWorkers
            })}
            disabled={!selectedCompany || assigning || departments.length === 0 || isOverLimit || pendingworker === 0}
            className="w-full lg:w-auto lg:self-center lg:px-16 py-2.5 sm:py-3 lg:py-3.5 text-sm sm:text-base lg:text-lg font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50"
          >
            {assigning ? "Allocating..." : "Allocate Department"}
          </button>

          {/* Worker list modal */}
          {showworkermodal && (
            <View_worker_supervisor
              showworkermodal={showworkermodal}
              setshowworkermodal={handleCloseWorkerModal}
              workerList={workerList}
              loadingWorkers={loadingWorkers}
              collapsedDepts={collapsedDepts}
              setCollapsedDepts={setCollapsedDepts}
              departments={departments}
              company={companies[0]}
              onWorkerUpdated={(attendanceId, newDepartmentName) =>
                handleWorkerUpdated(attendanceId, newDepartmentName, setWorkerList, setCollapsedDepts)
              }
              onRefetchWorkers={() => fetchWorkers(selectedCompany)}
            token={token}
    freezeDepartmentApi={freezeDepartmentApi}
    updateWorkerDepartmentApi={updateWorkerDepartmentApi}
            />
          )}

          {/* Success modal */}
          {showSuccessModal && assignResult && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-3 sm:p-4 z-[60]">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-[92vw] sm:max-w-sm p-5 sm:p-6 flex flex-col items-center gap-4 text-center max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 shrink-0">
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                </div>
                <div className="w-full">
                  <h2 className="text-sm sm:text-base font-semibold text-gray-900">
                    {assignResult.message || "Allocation completed"}
                  </h2>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                    <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                        Allocated: <span className="text-green-600">{assignResult.allocatedCount ?? 0}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                        Unallocated: <span className="text-amber-600">{assignResult.unallocatedCount ?? 0}</span>
                      </span>
                    </div>
                  </div>
                  {assignResult.remainingNeed &&
                    Object.values(assignResult.remainingNeed).some((v) => v > 0) && (
                      <div className="mt-3 flex flex-col gap-1 text-left">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                          Remaining Need
                        </span>
                        {Object.entries(assignResult.remainingNeed)
                          .filter(([, count]) => count > 0)
                          .map(([deptId, count]) => {
                            const dept = departments.find((d) => d._id === deptId);
                            return (
                              <div
                                key={deptId}
                                className="flex items-center justify-between gap-2 px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-lg text-xs"
                              >
                                <span className="text-gray-700 truncate">{dept?.name || deptId}</span>
                                <span className="font-semibold text-amber-600 shrink-0">{count}</span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                </div>
                <button
                  onClick={() => { setShowSuccessModal(false); setAssignResult(null); }}
                  className="w-full py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Error modal */}
          {showErrorModal && assignError && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-3 sm:p-4 z-[60]">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-[92vw] sm:max-w-sm p-5 sm:p-6 flex flex-col items-center gap-4 text-center max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-semibold text-gray-900">Assignment Failed</h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">{assignError}</p>
                </div>
                <button
                  onClick={() => { setShowErrorModal(false); setAssignError(null); }}
                  className="w-full py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AssignDepartment;